// Company signup and onboarding service for API calls
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';

const API_BASE_URL = API_CONFIG.API_BASE_URL;

// Types for company signup
export interface CompanySignupRequest {
  name: string;
  domain: string;
  contactEmail: string;
  industry?: string;
  status: string;
  planId: string | null;
  password: string;
  userName: string;
  encrypted?: boolean;
  platform_name?: string;
  platform_url?: string;
}

export interface CompanySignupResponse {
  success: boolean;
  data: {
    customerId: string;
    companyName: string;
    // null when the contact email belongs to a user removed from a company:
    // no session until the mailbox owner verifies the email.
    token: string | null;
    tokenId: string | null;
    zohoCustomerId: string | null;
    // PR #677 review (L2) — the backend also returns null for these in the removed-user
    // sign-up case (token is null); nullable to match, instead of hiding it with `as any`.
    planId: string | null;
    planName: string | null;
    isDefaultPlan: boolean | null;
  };
  message: string;
  code: number;
}

// Types for plans
export interface Plan {
  id: string;
  name: string;
  planCode: string;
  description: string;
  monthlyPrice: string;
  planCycles: number;
  features: string[];
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  zohoPlanId: string;
  zohoProductId: string;
  intervalCount: number;
  deletedAt: string | null;
  zohoPlanCode: string | null;
  zohoProductCode: string | null;
  zohoBillingCycle: string | null;
  updatedBy: string | null;
  intervalUnit: string;
  interval: number;
  createdBy: string | null;
  subscriberCount: string;
  createdByEmail: string;
  createdByRole: string;
  createdById: string;
}

export interface FetchPlansResponse {
  success: boolean;
  data: Plan[];
  message: string;
  code: number;
}

// Types for active plan/subscription response
export interface ActivePlan {
  plan_name: string;
  plan_code: string;
  billing_cycle: string;
  cost: string;
  currency_code: string;
  next_renewal: string;
  status: string;
  features: Record<string, string>; // Object with feature keys like "feature_1": "100 AI Responses"
  current_period_start: string;
  current_period_end: string;
  is_trial: boolean;
  trial_end: string | null;
}

export interface ActivePlanResponse {
  success?: boolean;
  data?: ActivePlan;
  message?: string;
  code?: number;
  // Direct response (if API doesn't wrap in success/data structure)
  plan_name?: string;
  plan_code?: string;
  billing_cycle?: string;
  cost?: string;
  currency_code?: string;
  next_renewal?: string;
  status?: string;
  features?: Record<string, string>;
  current_period_start?: string;
  current_period_end?: string;
  is_trial?: boolean;
  trial_end?: string | null;
}

// Types for subscription
export interface MySubscriptionResponse {
  success: boolean;
  data: Plan;
  message: string;
  code: number;
}

// Types for company details
export interface Company {
  id: string;
  name: string;
  domain?: string;
  contactEmail?: string;
  industry?: string;
  status?: string;
  [key: string]: any;
}

export interface CompanyResponse {
  id: string;
  name: string;
  domain?: string;
  contact_email?: string;
  industry?: string;
  status?: string;
  [key: string]: any;
}

// Company signup
export const createCompany = async (data: CompanySignupRequest): Promise<CompanySignupResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.COMPANY.SIGNUP}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Show the exact error message from the API response as-is
      const errorMessage = errorData.detail || errorData.message || `Company creation failed: ${response.status}`;
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Company creation error:', error);
    throw error;
  }
};

// Fetch plans
export const fetchPlans = async (): Promise<FetchPlansResponse> => {
  try {
    // Get platform_name from environment variable
    const platformName = 'prism7';
    
    // Build URL with platform_name query parameter if available
    const url = new URL(`${API_BASE_URL}${API_ENDPOINTS.COMPANY.FETCH_PLANS}`);
    if (platformName) {
      url.searchParams.append('platform_name', platformName);
    }
    
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'accept': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch plans: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Fetch plans error:', error);
    throw error;
  }
};



// Get subscription details
export const getMySubscription = async (authToken: string): Promise<MySubscriptionResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.COMPANY.MY_SUBSCRIPTION}`, {
      method: 'GET',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch subscription: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Get subscription error:', error);
    throw error;
  }
};

// Fetch active plans
export const fetchActivePlans = async (authToken?: string): Promise<ActivePlanResponse> => {
  try {
    const headers: HeadersInit = {
      'accept': 'application/json',
    };

    // Add authorization header if token is provided
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.SUBSCRIPTION_PLANS.ACTIVE_PLANS}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.detail || `Failed to fetch active plans: ${response.status}`);
    }

    const responseData = await response.json();
    
    // Log the response for debugging (can be removed in production)
    console.log('Active plans API response:', responseData);
    console.log('Response type:', typeof responseData);
    console.log('Response keys:', responseData ? Object.keys(responseData) : 'null/undefined');
    
    // Handle null or undefined response
    if (!responseData || typeof responseData !== 'object') {
      throw new Error('Invalid response: API returned null or non-object data');
    }
    
    // Handle both wrapped and direct response formats
    if (responseData.data && typeof responseData.data === 'object' && !Array.isArray(responseData.data)) {
      // Wrapped format: { success: true, data: {...}, message: "...", code: 200 }
      console.log('Detected wrapped format');
      return responseData as ActivePlanResponse;
    } else if (responseData.plan_name !== undefined || ('plan_name' in responseData)) {
      // Direct format: { plan_name: "...", plan_code: "...", ... }
      console.log('Detected direct format');
      return {
        success: true,
        data: responseData as ActivePlan,
        message: 'Active plan fetched successfully',
        code: 200,
      };
    } else {
      // Unexpected format - provide more details in error
      const responseKeys = responseData ? Object.keys(responseData) : [];
      console.error('Unexpected response format:', {
        responseData,
        keys: responseKeys,
        hasPlanName: 'plan_name' in responseData,
        hasData: 'data' in responseData,
      });
      throw new Error(
        `Unexpected response format from active plans API. ` +
        `Expected 'plan_name' or 'data' property, but received keys: [${responseKeys.join(', ')}]. ` +
        `Response preview: ${JSON.stringify(responseData).substring(0, 200)}...`
      );
    }
  } catch (error) {
    console.error('Fetch active plans error:', error);
    throw error;
  }
};

// Get company by ID
export const getCompanyById = async (companyId: string): Promise<CompanyResponse> => {
  try {
    // Use REFRESH_TOKEN as primary token (consistent with other services)
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || '';
    
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.COMPANY.GET_BY_ID(companyId)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.detail || `Failed to fetch company: ${response.status}`;
      
      // Provide more specific error message for 401
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Get company by ID error:', error);
    throw error;
  }
};

// Get my company (current user's company)
export const getMyCompany = async (): Promise<CompanyResponse> => {
  try {
    // Use REFRESH_TOKEN as primary token (consistent with other services)
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || '';
    
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.COMPANY.GET_MY_COMPANY}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.detail || `Failed to fetch company: ${response.status}`;
      
      // Provide more specific error message for 401
      if (response.status === 401) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Get my company error:', error);
    throw error;
  }
};